import React from 'react';
import {
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  styled,
  FormLabel,
  Box,
  lighten,
  SxProps,
} from '@mui/material';
import { EndAdornment } from '../PhoneInput/components/EndAdornment';

export type TextFieldProps = Omit<MuiTextFieldProps, 'variant' | 'hiddenLabel'> & {
  label?: string;
  showHelpIcon?: boolean;
  helpMessage?: string;
  inputStyles?: SxProps;
  size?: 'small' | 'medium';
  inputAriaLabel?: string;
  inputAriaDescribedBy?: string;
};

const StyledTextField = styled(MuiTextField)(({ theme, size, multiline }) => ({
  '& .MuiInputBase-root.Mui-disabled': {
    border: '1px solid #D0D5DD',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    padding: 0,
    '& .MuiInputBase-input': {
      borderRadius: '8px',
      backgroundColor: '#F2F4F7',
      padding: size === 'medium' ? '12px' : '8px',
      color: '#667085 !important',
      WebkitTextFillColor: '#667085 !important',
    },
    '& input': {
      color: '#667085 !important',
      WebkitTextFillColor: '#667085 !important',
    },
  },
  '& .MuiInputBase-root.Mui-error': {
    borderColor: '#FDA29B',
    '& .MuiInputBase-input': {
      borderColor: '#FDA29B',
    },
  },
  margin: 0,
  '& .MuiInputBase-root': {
    paddingRight: 0,
    paddingLeft: 0,
    '&:before, &:after': {
      display: 'none',
    },
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #D0D5DD',
    transition: 'box-shadow 0.2s ease-in-out',
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${lighten(theme.palette.primary.main, 0.4)}, 0 0 0 4px ${lighten(
        theme.palette.primary.main,
        0.8,
      )}`,
      '&.Mui-error': {
        boxShadow: `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(240, 68, 56, 0.24)`,
      },
      backgroundColor: '#fff',
      outline: 'none',
      borderRadius: '8px !important',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff !important',
      borderRadius: '8px',
    },
  },
  '& .MuiInputBase-input': {
    padding: size === 'medium' ? '12px' : '8px',
    // Clipping to an ellipsis belongs to a single-line input. In multiline mode this selector is the
    // textarea, and MUI deliberately clears its own inline `overflow` once the content passes `maxRows` so
    // the element can scroll — hiding the overflow here filled that gap and swallowed the extra lines.
    ...(multiline ? {} : { overflow: 'hidden', textOverflow: 'ellipsis' }),
  },
  '& .MuiInputAdornment-root': {
    marginTop: '0 !important',
    marginLeft: '12px',
    marginRight: '8px',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '2px',
  },
  'input:-webkit-autofill': {
    borderRadius: '8px',
    transition: 'background-color 5000s',
  },
  '& .MuiInputBase-multiline': {
    padding: '0',
  },
}));

/**
 * Renders a TextField component with an external label
 */
export const TextField: React.FC<React.PropsWithChildren<TextFieldProps>> = ({
  label,
  size = 'medium',
  inputStyles,
  inputAriaLabel,
  showHelpIcon,
  helpMessage,
  ...props
}) => {
  const showErrorIcon = Boolean(props.error) && !props.helperText;

  return (
    <Box sx={{ width: props.fullWidth ? '100%' : 'auto' }}>
      {label && (
        <FormLabel
          sx={{
            ml: '2px',
            mb: '4px',
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            color: '#344054',
          }}
          htmlFor={props.id}
          error={props.error}
          required={props.required}
        >
          {label}
        </FormLabel>
      )}
      <StyledTextField
        {...props}
        size={size}
        variant="filled"
        slotProps={{
          ...props.slotProps,
          input: {
            'aria-label': inputAriaLabel,
            sx: {
              ...inputStyles,
            },
            ...(props.slotProps?.input || {}),
            endAdornment:
              props.slotProps?.input && 'endAdornment' in props.slotProps?.input ? (
                <>
                  <EndAdornment
                    error={showErrorIcon}
                    showHelpIcon={showHelpIcon || false}
                    helpMessage={helpMessage || ''}
                  />
                  {props.slotProps.input.endAdornment}
                </>
              ) : (
                <EndAdornment
                  error={showErrorIcon}
                  showHelpIcon={showHelpIcon || false}
                  helpMessage={helpMessage || ''}
                />
              ),
            startAdornment:
              props.slotProps?.input && 'startAdornment' in props.slotProps?.input
                ? props.slotProps.input.startAdornment
                : undefined,
          },
        }}
        sx={{
          '& .MuiInputBase-input': {
            whiteSpace: props.multiline ? 'pre-wrap' : 'nowrap',
          },
          ...props.sx,
        }}
      />
    </Box>
  );
};
